import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { inr, formatApiErrorDetail, gstPct } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CreditCard, Smartphone, Building2, Wallet, Lock, CheckCircle2, ShieldCheck } from "lucide-react";

const METHODS = [
  { id: "card", label: "Credit / Debit card", icon: CreditCard },
  { id: "upi", label: "UPI", icon: Smartphone },
  { id: "netbanking", label: "Net banking", icon: Building2 },
];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Payment() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [config, setConfig] = useState(null);
  const [method, setMethod] = useState("card");
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(null); // { payment_id }
  const [card, setCard] = useState({ number: "", name: "", expiry: "", cvv: "" });
  const [upi, setUpi] = useState("");
  const [bank, setBank] = useState("HDFC Bank");

  useEffect(() => {
    (async () => {
      try {
        const [b, c] = await Promise.all([
          api.get(`/bookings/${bookingId}`).then((r) => r.data),
          api.get(`/config`).then((r) => r.data),
        ]);
        setBooking(b);
        setConfig(c);
        if (b.status === "confirmed" && b.payment_id) setSuccess({ payment_id: b.payment_id });
      } catch (err) {
        toast.error("Could not load payment details");
        navigate("/bookings");
      }
    })();
  }, [bookingId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const validMethod = () => {
    if (method === "card") return card.number.replace(/\s/g, "").length >= 12 && card.name && card.expiry && card.cvv.length >= 3;
    if (method === "upi") return /.+@.+/.test(upi);
    return !!bank;
  };

  const pay = async () => {
    const rzpMode = config?.razorpay_enabled;
    if (!rzpMode && !validMethod()) { toast.error("Please complete the payment details"); return; }
    setPaying(true);
    try {
      if (rzpMode) {
        const ok = await loadRazorpay();
        if (!ok) throw new Error("Could not load payment gateway");
        let orderId = booking?.razorpay_order_id;
        let keyId = config?.razorpay_key_id;
        if (!orderId) {
          const { data } = await api.post(`/payment/create-order/${booking.id}`);
          orderId = data.razorpay_order_id;
          keyId = data.razorpay_key_id || keyId;
        }
        const rzp = new window.Razorpay({
          key: keyId,
          amount: Math.round(booking.total_price * 100),
          currency: "INR",
          name: "Ixitrip",
          description: `${booking.hotel_name} · ${booking.room_name}`,
          order_id: orderId,
          handler: async (resp) => {
            try {
              await api.post("/payment/verify", {
                booking_id: booking.id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              setSuccess({ payment_id: resp.razorpay_payment_id });
            } catch (err) {
              toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Payment verification failed");
            }
          },
          prefill: { name: booking.guest_name, email: booking.guest_email, contact: booking.guest_phone },
          theme: { color: "#FF385C" },
          modal: { ondismiss: () => setPaying(false) },
        });
        rzp.on("payment.failed", (r) => {
          toast.error(r?.error?.description || "Payment failed. Please try again.");
          setPaying(false);
        });
        rzp.open();
        setPaying(false);
        return;
      }
      // Mock gateway (only when Razorpay is not enabled)
      await new Promise((r) => setTimeout(r, 1200));
      const { data } = await api.post(`/payment/mock-confirm/${booking.id}`);
      setSuccess({ payment_id: data.payment_id });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPaying(false);
    }
  };

  if (!booking) return <div className="py-20 text-center text-[#717171]">Loading...</div>;

  const reservation = booking.room_total ?? 0;
  const gst = booking.gst_amount ?? 0;
  const conv = booking.convenience_fee ?? booking.platform_fee ?? 0;
  const convGst = booking.convenience_fee_gst ?? 0;
  const convGstRate = booking.convenience_fee_gst_rate ?? 0.18;
  const total = booking.total_price ?? reservation + gst + conv + convGst;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-display font-bold mb-1">Secure payment</h1>
      <p className="text-[#717171] mb-6 flex items-center gap-1 text-sm"><ShieldCheck className="w-4 h-4 text-[#008A05]" /> 256-bit encrypted · {config?.razorpay_enabled ? "Razorpay" : "Test mode"}</p>

      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-white border border-[#EBEBEB] rounded-2xl p-6">
            {config?.razorpay_enabled ? (
              <div data-testid="razorpay-panel" className="text-center py-4">
                <div className="w-14 h-14 rounded-2xl bg-[#FFF2F4] flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="w-7 h-7 text-[#FF385C]" />
                </div>
                <h2 className="font-display font-semibold text-lg mb-1">Pay securely with Razorpay</h2>
                <p className="text-sm text-[#717171] max-w-sm mx-auto">Click below to open Razorpay's secure checkout. You can pay by UPI, card, net banking or wallet — your details are entered directly on Razorpay.</p>
                <div className="flex items-center justify-center gap-3 mt-4 text-[#717171]">
                  <Smartphone className="w-5 h-5" /><CreditCard className="w-5 h-5" /><Building2 className="w-5 h-5" /><Wallet className="w-5 h-5" />
                </div>
              </div>
            ) : (
              <>
                <h2 className="font-display font-semibold text-lg mb-4">Choose a payment method</h2>
                <div className="grid grid-cols-3 gap-2 mb-5">
                  {METHODS.map((m) => {
                    const Icon = m.icon;
                    return (
                      <button key={m.id} type="button" data-testid={`pay-method-${m.id}`} onClick={() => setMethod(m.id)}
                        className={`flex flex-col items-center gap-1.5 rounded-xl border-2 px-2 py-3 text-xs font-medium transition-colors ${method === m.id ? "border-[#FF385C] bg-[#FFF2F4] text-[#FF385C]" : "border-[#EBEBEB] text-[#717171] hover:border-[#DDD]"}`}>
                        <Icon className="w-5 h-5" /> {m.label}
                      </button>
                    );
                  })}
                </div>

                {method === "card" && (
                  <div className="grid sm:grid-cols-2 gap-3" data-testid="pay-card-form">
                    <div className="sm:col-span-2"><Label className="text-sm mb-1 block">Card number</Label><Input data-testid="pay-card-number" inputMode="numeric" placeholder="4111 1111 1111 1111" value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} className="rounded-xl" /></div>
                    <div className="sm:col-span-2"><Label className="text-sm mb-1 block">Name on card</Label><Input data-testid="pay-card-name" placeholder="As printed on card" value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })} className="rounded-xl" /></div>
                    <div><Label className="text-sm mb-1 block">Expiry (MM/YY)</Label><Input data-testid="pay-card-expiry" placeholder="08/28" value={card.expiry} onChange={(e) => setCard({ ...card, expiry: e.target.value })} className="rounded-xl" /></div>
                    <div><Label className="text-sm mb-1 block">CVV</Label><Input data-testid="pay-card-cvv" type="password" inputMode="numeric" placeholder="123" value={card.cvv} onChange={(e) => setCard({ ...card, cvv: e.target.value })} className="rounded-xl" /></div>
                  </div>
                )}
                {method === "upi" && (
                  <div data-testid="pay-upi-form"><Label className="text-sm mb-1 block">UPI ID</Label><Input data-testid="pay-upi-id" placeholder="yourname@upi" value={upi} onChange={(e) => setUpi(e.target.value)} className="rounded-xl" /></div>
                )}
                {method === "netbanking" && (
                  <div data-testid="pay-netbanking-form">
                    <Label className="text-sm mb-1 block">Select bank</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {["HDFC Bank", "ICICI Bank", "SBI", "Axis Bank", "Kotak", "Yes Bank"].map((b) => (
                        <button key={b} type="button" data-testid={`pay-bank-${b}`} onClick={() => setBank(b)} className={`rounded-xl border px-3 py-2 text-xs ${bank === b ? "border-[#FF385C] bg-[#FFF2F4] text-[#FF385C]" : "border-[#EBEBEB] text-[#717171]"}`}>{b}</button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            <Button data-testid="pay-now-btn" onClick={pay} disabled={paying} className="w-full mt-6 rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white h-12 text-base font-semibold">
              <Lock className="w-4 h-4 mr-2" /> {paying ? "Processing payment..." : `Pay ${inr(total)}`}
            </Button>
          </div>
        </div>

        <aside className="lg:col-span-2">
          <div className="bg-white border border-[#EBEBEB] rounded-2xl p-6 sticky top-28">
            <h3 className="font-display font-semibold mb-1">{booking.hotel_name}</h3>
            <p className="text-sm text-[#717171] mb-4">{booking.room_name}</p>
            <hr className="border-[#EBEBEB] my-3" />
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Total price of reservation</span><span data-testid="pay-bd-reservation">{inr(reservation)}</span></div>
              <div className="flex justify-between"><span>Taxes — {gstPct(booking.gst_rate || 0)} GST</span><span data-testid="pay-bd-gst">{inr(gst)}</span></div>
              <div className="flex justify-between"><span>Ixitrip convenience fee</span><span data-testid="pay-bd-convenience">{inr(conv)}</span></div>
              <div className="flex justify-between"><span>GST on convenience fee — {gstPct(convGstRate)}</span><span data-testid="pay-bd-convenience-gst">{inr(convGst)}</span></div>
            </div>
            <hr className="border-[#EBEBEB] my-3" />
            <div className="flex justify-between font-display font-bold text-lg">
              <span>Total amount to be paid</span><span data-testid="pay-total">{inr(total)}</span>
            </div>
            <div data-testid="pay-cancellation-summary" className="mt-4 rounded-xl bg-[#F7F7F9] border border-[#EBEBEB] p-3 text-xs text-[#717171] leading-relaxed">
              <span className="font-semibold text-[#222]">Cancellation policy:</span> Full refund if you cancel 48+ hours before check-in; 50% refund within 48 hours. <a href="/cancellation-policy" target="_blank" rel="noreferrer" className="text-[#FF385C] font-medium">Read more</a>
            </div>
          </div>
        </aside>
      </div>

      <Dialog open={!!success} onOpenChange={(o) => { if (!o) navigate("/bookings"); }}>
        <DialogContent className="sm:max-w-md text-center" data-testid="payment-success-dialog">
          <div className="flex flex-col items-center py-4">
            <CheckCircle2 className="w-16 h-16 text-[#008A05] mb-3" />
            <DialogTitle className="text-2xl font-display font-bold mb-1">Payment successful!</DialogTitle>
            <p className="text-[#717171] text-sm mb-4">Your reservation is confirmed.</p>
            <div className="w-full bg-[#F7F7F9] rounded-xl p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-[#717171]">Payment ID</span><span className="font-mono font-semibold" data-testid="payment-id">{success?.payment_id}</span></div>
              <div className="flex justify-between"><span className="text-[#717171]">Amount paid</span><span className="font-semibold">{inr(total)}</span></div>
              <div className="flex justify-between"><span className="text-[#717171]">Property</span><span className="font-semibold truncate ml-2">{booking.hotel_name}</span></div>
            </div>
            <Button data-testid="view-bookings-btn" onClick={() => navigate("/bookings")} className="w-full mt-5 rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white">View my bookings</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
